import { FC, useContext, useState } from "react";
import {
  Divider,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemText,
  Stack,
  Tooltip,
  Typography
} from "@mui/material";
import { Delete as DeleteIcon } from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import LaunchIcon from "@mui/icons-material/Launch";
import { AxiosResponse } from "axios";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { Dialog } from "components/Dialog";
import { Override, Report } from "pages/Kpis/TurbineRow";
import { ConfirmationDialog } from "components/ConfirmationDialog";
import { API_URL_KPIS } from "constants/api_urls";
import { AxiosContext } from "contexts/AxiosContext";
import { SnackbarQueueContext } from "contexts/SnackbarQueueContext";
import { DateFormatter } from "components/DateFormatter";
import { MeasurementUpdates } from "interfaces/kpis";

export interface Props {
  isOpen: boolean;
  onClose: () => void;
  targetId: number;
  override: Override | undefined;
  report: Report | undefined;
  measurements: MeasurementUpdates[] | undefined;
}

export const ViewEvidenceAnnualKpiDialog: FC<Props> = ({
  isOpen,
  onClose,
  targetId,
  override,
  report,
  measurements
}): JSX.Element => {
  const { axios } = useContext(AxiosContext)!;
  const queryClient = useQueryClient();
  const { addSnackbarToQueue } = useContext(SnackbarQueueContext);

  const [
    showRemoveOverrideConfirmation,
    setShowRemoveOverrideConfirmation
  ] = useState(false);

  const closeAndResetDialog = (): void => {
    onClose();
  };

  let dialogTitle = "Report";
  if (override?.justification) {
    dialogTitle = "Override Justification";
  }
  if (measurements?.length) {
    dialogTitle = "Measurements";
  }

  const removeOverrideMutation = useMutation({
    mutationFn: async () => {
      return axios.patch<number, AxiosResponse<unknown>>(
        `${API_URL_KPIS}/targets/${targetId}`,
        { override_justification: null }
      );
    },
    onSuccess: async () => {
      addSnackbarToQueue({
        message: "Override was successfully removed.",
        severity: "success"
      });

      await queryClient.invalidateQueries(["kpis", "progress"]);
      await queryClient.invalidateQueries(["kpis", "targets"]);
    },
    onError: () => {
      addSnackbarToQueue({
        message: "Failed to remove override.",
        severity: "error"
      });
    }
  });

  const handleRemoveOverride = (): void => {
    setShowRemoveOverrideConfirmation(true);
  };

  return (
    <>
      <Dialog
        dialogId="view-evidence-annualkpi-dialog"
        title={dialogTitle}
        open={isOpen}
        size="medium"
        fullWidth={true}
        onClose={closeAndResetDialog}
        data-qa="KPI-View-Evidence-AnnualKpi-Dialog"
        confirmButton={<></>} // stupid workaround to remove the confirm button
        allowClickaway
      >
        <StyledDialogContent>
          {report?.link && (
            <Stack direction="column" gap={1} alignItems="baseline">
              <Typography>
                <StyledMuiLink
                  href={report.link}
                  target="_blank"
                  rel="noopener external"
                >
                  {report.title}
                  <LaunchIcon fontSize="small" />
                </StyledMuiLink>
              </Typography>
              <Typography variant="body2" fontStyle="italic">
                {report.userName} on{" "}
                <DateFormatter date={report.date} showTime />
              </Typography>
            </Stack>
          )}

          {override?.justification && (
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Stack gap={1}>
                <Typography sx={{ whiteSpace: "pre-wrap" }}>
                  {override.justification}
                </Typography>
                <Typography variant="body2" fontStyle="italic">
                  {override.userName} on{" "}
                  <DateFormatter date={override.date} showTime />
                </Typography>
              </Stack>
              <Tooltip title="Remove this override">
                <IconButton onClick={handleRemoveOverride} size="small">
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          )}

          {measurements && (
            <>
              <List>
                {measurements.map((m, index) => (
                  <>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemText
                        primary={
                          <Stack>
                            <Typography>
                              Sensor: <strong>{m.sensor_name}</strong>
                            </Typography>
                            <Typography variant="body2" fontStyle="italic">
                              <DateFormatter
                                date={m.measurement_date}
                                showTime
                              />
                            </Typography>
                          </Stack>
                        }
                      />
                    </ListItem>
                    {index < measurements.length - 1 && <Divider />}
                  </>
                ))}
              </List>
            </>
          )}
        </StyledDialogContent>
      </Dialog>

      {override?.justification && showRemoveOverrideConfirmation && (
        <ConfirmationDialog
          title="Are you sure you want to remove this override?"
          open={showRemoveOverrideConfirmation}
          onClose={() => setShowRemoveOverrideConfirmation(false)}
          onConfirm={async () => {
            await removeOverrideMutation.mutate();
            setShowRemoveOverrideConfirmation(false);
          }}
        />
      )}
    </>
  );
};

const StyledMuiLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing(1)};
  align-items: flex-start;
`;
StyledMuiLink.displayName = "StyledMuiLink";

const StyledDialogContent = styled("div")`
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  gap: ${props => props.theme.spacing(2)};
`;
StyledDialogContent.displayName = "StyledDialogContent";
